import { ArrowLeftOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate, useParams,
} from 'react-router-dom';

import {
    objectionsMutations, objectionsQueries,
} from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, ObjectionForm, ObjectionFormRef,
} from '@/components';
import { AppRoute } from '@/constants';

export const ObjectionEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const formRef = useRef<ObjectionFormRef>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        title: string;
        body?: string | null;
        priority?: number | null;
    } | null>(null);

    const { data: objection, isLoading } = useQuery({
        ...objectionsQueries.getOneObjection(id || ''),
        refetchOnWindowFocus: false,
    });
    const updateMutationOptions = objectionsMutations.update(id || '');
    const { mutate: updateObjection, isPending } = useMutation(updateMutationOptions);

    const handleSubmit = useCallback((data: {
        title: string;
        body?: string | null;
        priority?: number | null;
    }) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            updateObjection(formData, {
                onSuccess: () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    formRef.current?.reset();
                },
            });
        }
    }, [formData, updateObjection]);

    const handleCancel = useCallback(() => {
        navigate(`${AppRoute.Objections}/${id}`);
    }, [navigate, id]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Редагувати заперечення</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={12}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Objections}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Редагувати заперечення
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <ObjectionForm
                            ref={formRef}
                            initData={objection || null}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isLoading || isPending}
                            cancelTo={`${AppRoute.Objections}/${id}`}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="objection"
                entityName={formData?.title || objection?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
